import { createSignal, Show } from "solid-js";
import type { JSX } from "solid-js";
import { A, useLocation } from "@solidjs/router";
import { AnvilMark } from "./public/Nav";
import { currentUser, logout, startLogin } from "../lib/auth";
import { RoleBadge } from "./rbac/RoleBadge";
import { effectiveUserRole } from "../lib/rbac";
import { useVcsEnabled } from "../lib/vcsSettings";

interface ShellProps {
  children: JSX.Element;
}

export function Shell(props: ShellProps) {
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = createSignal(false);
  const user = () => currentUser();
  const role = () => effectiveUserRole();

  const repoMatch = () => loc.pathname.match(/^\/repos\/([^/]+)\/([^/]+)/);
  const currentOwner = () => repoMatch()?.[1] || user()?.tenant_id || "";
  const vcsEnabled = useVcsEnabled(currentOwner);

  const navLinks = () => {
    const isVcs = vcsEnabled();
    const rm = repoMatch();
    const repoBase = rm ? `/repos/${rm[1]}/${rm[2]}` : "/repos";

    if (isVcs) {
      return [
        { to: "/dashboard", label: "Dashboard", match: (p: string) => p === "/dashboard" },
        { to: "/repos", label: "Repos", match: (p: string) => p === "/repos" || (!rm && p.startsWith("/repos")) },
        { to: "/models", label: "Models", match: (p: string) => p === "/models" || p.startsWith("/models") },
        { to: "/ovc", label: "OVC", match: (p: string) => p === "/ovc" || p.startsWith("/ovc/") },
        ...(rm
          ? [
              {
                to: repoBase,
                label: "Code",
                match: (p: string) =>
                  p === repoBase || p.startsWith(`${repoBase}/tree`) || p.startsWith(`${repoBase}/blob`),
              },
              {
                to: `${repoBase}/pulls`,
                label: "Pull Requests",
                match: (p: string) => p.includes("/pulls"),
              },
            ]
          : []),
        {
          to: rm ? `${repoBase}/releases` : "/releases",
          label: "Releases",
          match: (p: string) => p.includes("/releases"),
        },
        { to: "/settings", label: "Settings", match: (p: string) => p.startsWith("/settings") },
      ];
    } else {
      return [
        { to: "/dashboard", label: "Dashboard", match: (p: string) => p === "/dashboard" },
        { to: "/repos", label: "Repos", match: (p: string) => p === "/repos" || (!rm && p.startsWith("/repos")) },
        { to: "/models", label: "Models", match: (p: string) => p === "/models" || p.startsWith("/models") },
        { to: "/ovc", label: "OVC", match: (p: string) => p === "/ovc" || p.startsWith("/ovc/") },
        ...(rm
          ? [
              {
                to: `${repoBase}/intelligence`,
                label: "MCP Intelligence",
                match: (p: string) => p.includes("/intelligence") || p.includes("/search") || p.includes("/proposals"),
              },
            ]
          : []),
        {
          to: rm ? `${repoBase}/releases` : "/releases",
          label: "Releases",
          match: (p: string) => p.includes("/releases"),
        },
        { to: "/settings", label: "Settings", match: (p: string) => p.startsWith("/settings") },
      ];
    }
  };

  return (
    <div class="min-h-full flex flex-col">
      <header class="border-b border-anvil-700 bg-anvil-800/60 backdrop-blur sticky top-0 z-10">
        <div class="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between sm:justify-start gap-y-4 gap-x-4 sm:gap-x-6">
          <A href="/" class="flex items-center gap-3 text-anvil-50 font-bold tracking-tight no-underline hover:opacity-85 transition-opacity shrink-0 order-1">
            <AnvilMark class="h-16 w-16" />
            <div class="flex flex-col leading-snug">
              <span class="font-disp text-[13px] sm:text-[14px] font-bold uppercase tracking-[0.14em]">
                Throughput Lines
              </span>
              <span class="font-disp text-[13px] sm:text-[14px] font-bold uppercase tracking-[0.14em]">
                Of Code Per Day
              </span>
            </div>
          </A>
          <div class="flex items-center gap-4 order-2 sm:order-3 sm:ml-auto">
            <Show 
              when={user()} 
              fallback={
                <button 
                  class="text-xs font-semibold px-3 py-1.5 border border-anvil-600 rounded bg-anvil-800 text-anvil-300 hover:text-anvil-50 hover:border-anvil-500 transition shrink-0" 
                  onClick={() => startLogin()}
                >
                  Sign in
                </button>
              }
            >
              <div class="relative flex items-center gap-2.5">
                <RoleBadge role={role()} size="sm" />
                <button
                  onClick={() => setMenuOpen(!menuOpen())}
                  class="flex items-center gap-2 focus:outline-none shrink-0"
                  title="Profile Menu"
                >
                  <div class="h-8 w-8 rounded-full bg-anvil-700 flex items-center justify-center font-bold text-anvil-50 border border-anvil-600 hover:border-anvil-400 transition">
                    {user()?.display_name?.charAt(0).toUpperCase() || user()?.email?.charAt(0).toUpperCase() || "?"}
                  </div>
                </button>
                <Show when={menuOpen()}>
                  <div class="absolute right-0 top-full mt-2 w-52 bg-anvil-800 border border-anvil-700 rounded-md shadow-xl py-1 z-50">
                    <div class="px-4 py-2 border-b border-anvil-700 space-y-1">
                      <div class="text-sm font-semibold text-anvil-50 truncate">{user()?.display_name || user()?.email || "User"}</div>
                      <div class="text-[10px] text-anvil-400 truncate uppercase tracking-wider">Org: {user()?.tenant_id}</div>
                      <div class="pt-0.5">
                        <RoleBadge role={role()} size="sm" />
                      </div>
                    </div>
                    <button
                      onClick={() => logout()}
                      class="block w-full text-left px-4 py-2 text-sm text-anvil-300 hover:bg-anvil-700 hover:text-anvil-50 transition"
                    >
                      Log out
                    </button>
                  </div>
                </Show>
              </div>
            </Show>
          </div>
          <nav class="w-full sm:w-auto flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0 order-3 sm:order-2 sm:ml-4">
            {navLinks().map((l) => (
              <A
                href={l.to}
                class="nav-link whitespace-nowrap"
                classList={{ "nav-link-active": l.match?.(loc.pathname) ?? false }}
              >
                {l.label}
              </A>
            ))}
          </nav>
        </div>
      </header>
      <main class="flex-1 max-w-7xl w-full mx-auto px-4 py-6">{props.children}</main>
      <footer class="border-t border-anvil-700 text-anvil-500 text-xs py-3 text-center">
        Open core at{" "}
        <a href="https://github.com/s7-works/ganvil" class="underline">github.com/s7-works/ganvil</a>.
      </footer>
    </div>
  );
}
